import { useFormik } from 'formik';
import slugify from 'slugify';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card, Col, Input, Row,
} from 'reactstrap';
import * as Yup from 'yup';
import FormField from 'components/AddLocation/FormField';
import InputValidation from 'components/App/InputValidation';
import Button from 'components/App/Button';
import discoverCategoryApi from 'api/discoverCategory';
import { getMessageError, setFormErrorsOrHandleError } from 'utils';
import { toast } from 'react-toastify';
import mediaApi from 'api/mediaApi';
import AppFormProfilePicture from 'components/App/AppFormProfilePicture';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { Pencil } from 'lucide-react';

function SlugInput(props) {
  const { children, defaultValue } = props;
  const [isDisabled, setIsDisabled] = useState(defaultValue);
  return children({ isDisabled, setIsDisabled });
}

function DiscoverCategoryDetails({
  isCreate,
  categoryDetails,
  callback,
  isModal,
  onCloseModal,
}) {
  const { id } = useParams();

  const isEdit = !isCreate;

  const [file, setFile] = React.useState(null);

  const details = isCreate ? {} : categoryDetails;

  const validation = useFormik({
    initialValues: {
      name: details?.name || '',
      nameEN: details?.nameEN || '',
      description: details?.description || '',
      descriptionEN: details?.descriptionEN || '',
      slug: details?.slug || '',
      isPublished: details?.isPublished || false,
      previewImage: details?.previewImage || '',
    },

    validationSchema: Yup.object({
      name: Yup
        .string()
        .required('Vui lòng nhập tên hạng mục')
        .max(100, 'Tên hạng mục không quá 100 ký tự'),
      nameEN: Yup
        .string()
        .required('Vui lòng nhập tên hạng mục')
        .max(100, 'Tên hạng mục không quá 100 ký tự'),
      description: Yup
        .string()
        .required('Vui lòng nhập mô tả hạng mục')
        .max(2000, 'Mô tả không quá 2000 ký tự'),
      descriptionEN: Yup
        .string()
        .required('Vui lòng nhập mô tả hạng mục')
        .max(2000, 'Mô tả không quá 2000 ký tự'),
      previewImage: Yup
        .mixed()
        .required('Vui lòng chọn ảnh'),
    }),

    onSubmit: async (values) => {
      try {
        // eslint-disable-next-line no-nested-ternary
        const [, image] = file
          ? await mediaApi
            .uploadMultipleMedia([{ file }])
            .then((r) => [null, r.payload[0]])
            .catch((e) => [getMessageError(e), null])
          : details.previewImage
            ? [null, details.previewImage]
            : [null, null];

        // eslint-disable-next-line no-unused-vars
        const r = isCreate
          ? await discoverCategoryApi.createDiscoverCategory({
            ...values,
            previewImage: image,
          })
          : await discoverCategoryApi.updateDiscoverCategory(id, {
            ...values,
            previewImage: image,
          });
        toast.success(isCreate ? 'Tạo thành công' : 'Cập nhật thành công');
        await callback?.();
      } catch (error) {
        setFormErrorsOrHandleError(error, validation);
      }
    },
  });

  return (
    <Row className="justify-content-center">
      <Col lg={isCreate ? 12 : 5}>
        <Card className="p-3">
          <form onSubmit={validation.handleSubmit}>
            <FormField label="Tên hạng mục - VI" isRequired>
              <InputValidation
                isCustomOnChange
                name="name"
                placeholder="Nhập tên hạng mục"
                validation={validation}
                onChange={(e) => {
                  validation.handleChange(e);

                  if (!isEdit) {
                    validation.setFieldValue(
                      'slug',
                      slugify(e.target.value, '-').toLowerCase(),
                    );
                  }
                }}
                limitCharacter={100}
                isShowCounter
              />
            </FormField>

            <FormField label="Tên hạng mục - EN" isRequired>
              <InputValidation
                name="nameEN"
                placeholder="Nhập tên hạng mục"
                validation={validation}
                onChange={validation.handleChange}
                limitCharacter={100}
                isShowCounter
              />
            </FormField>

            <FormField label="Mô tả hạng mục - VI" isRequired>
              <InputValidation
                type="textarea"
                name="description"
                placeholder="Nhập mô tả hạng mục"
                validation={validation}
                onChange={validation.handleChange}
                limitCharacter={2000}
                style={{ minHeight: '84px' }}
                isShowCounter
              />
            </FormField>

            <FormField label="Mô tả hạng mục - EN" isRequired>
              <InputValidation
                type="textarea"
                name="descriptionEN"
                placeholder="Nhập mô tả hạng mục"
                validation={validation}
                onChange={validation.handleChange}
                limitCharacter={2000}
                style={{ minHeight: '84px' }}
                isShowCounter
              />
            </FormField>

            <FormField label="Slug" isRequired>
              <SlugInput defaultValue>
                {({ isDisabled, setIsDisabled }) => (
                  <div className="position-relative">
                    <InputValidation
                      name="slug"
                      type="text"
                      placeholder="Nhập slug"
                      validation={validation}
                      isCustomOnChange
                      onChange={(e) => {
                        validation.setFieldValue(
                          'slug',
                          slugify(e.target.value, '-').toLowerCase(),
                        );
                      }}
                      isDisabled={isDisabled}
                      style={{ paddingRight: '30px' }}
                    />

                    <button
                      type="button"
                      onClick={() => setIsDisabled((prev) => !prev)}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '4px 10px',
                      }}
                    >
                      <Pencil width={16} height={16} />
                    </button>
                  </div>
                )}
              </SlugInput>
            </FormField>

            <FormField label="Icon" required>
              <AppFormProfilePicture
                file={file}
                setFile={(f) => {
                  setFile(f);
                  validation.setFieldValue('previewImage', f);
                }}
                defaultValue={details.previewImage}
                validateErrorMessage={validation.errors.previewImage}
              />
            </FormField>

            <label
              htmlFor="isPublished"
              className="d-flex align-items-center gap-2"
            >
              <Input
                name="isPublished"
                id="isPublished"
                type="checkbox"
                checked={validation.values.isPublished}
                onChange={(e) => validation.setFieldValue('isPublished', e.target.checked)}
              />
              <span>Xuất bản</span>
            </label>

            <div className="d-flex justify-content-end gap-2">
              {isModal && (
                <Button
                  classes="custom_button_cancel"
                  type="button"
                  onClick={() => onCloseModal()}
                  style={{ width: '120px' }}
                  outline
                >
                  Huỷ
                </Button>
              )}

              <Button
                // classes="w-100"
                type="submit"
                loading={validation.isSubmitting}
                disabled={validation.isSubmitting}
                style={{ width: '120px' }}
              >
                {isCreate ? 'Lưu' : 'Cập nhật'}
              </Button>
            </div>
          </form>
        </Card>
      </Col>
    </Row>
  );
}

export default DiscoverCategoryDetails;

DiscoverCategoryDetails.propTypes = {
  categoryDetails: PropTypes.instanceOf(Object),
  isCreate: PropTypes.bool.isRequired,
  callback: PropTypes.func,
  isModal: PropTypes.bool,
  onCloseModal: PropTypes.func,
};

DiscoverCategoryDetails.defaultProps = {
  categoryDetails: {},
  callback: () => { },
  isModal: false,
  onCloseModal: () => { },
};
